import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "/api";

const api = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

// Request interceptor - attach token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data: { email: string; password: string }) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
  register: (data: Record<string, unknown>) => api.post("/auth/register", data),
  seed: () => api.post("/auth/seed"),
};

// Users
export const usersAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/users", { params }),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (data: Record<string, unknown>) => api.post("/auth/register", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
  getStats: () => api.get("/users/stats/overview"),
  getClassAssignments: () => api.get("/users/class-assignments"),
  assignClasses: (id: string, data: { assignedClasses?: string[]; classTeacherOf?: string }) => api.put(`/users/${id}/assign-classes`, data),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get("/dashboard/stats"),
};

// Training
export const trainingAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/training", { params }),
  create: (data: Record<string, unknown>) => api.post("/training", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/training/${id}`, data),
  delete: (id: string) => api.delete(`/training/${id}`),
  getCompliance: (params?: Record<string, unknown>) => api.get("/training/compliance", { params }),
};

// Interventions
export const interventionsAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/interventions", { params }),
  create: (data: Record<string, unknown>) => api.post("/interventions", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/interventions/${id}`, data),
  getStats: () => api.get("/interventions/stats"),
};

// Counselling
export const counsellingAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/counselling", { params }),
  create: (data: Record<string, unknown>) => api.post("/counselling", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/counselling/${id}`, data),
  delete: (id: string) => api.delete(`/counselling/${id}`),
  getStats: () => api.get("/counselling/stats"),
};

// SEL
export const selAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/sel", { params }),
  create: (data: Record<string, unknown>) => api.post("/sel", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/sel/${id}`, data),
  getStats: () => api.get("/sel/stats"),
};

// Events
export const eventsAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/events", { params }),
  create: (data: Record<string, unknown>) => api.post("/events", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/events/${id}`, data),
  delete: (id: string) => api.delete(`/events/${id}`),
  getStats: () => api.get("/events/stats"),
};

// Referrals
export const referralsAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/referrals", { params }),
  create: (data: Record<string, unknown>) => api.post("/referrals", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/referrals/${id}`, data),
  getStats: () => api.get("/referrals/stats"),
};

// Students
export const studentsAPI = {
  submitMood: (data: Record<string, unknown>) => api.post("/students/mood", data),
  getMoodHistory: (studentId: string) => api.get(`/students/mood/${studentId}`),
  getProfile: (studentId: string) => api.get(`/students/profile/${studentId}`),
  updateProfile: (studentId: string, data: Record<string, unknown>) => api.put(`/students/profile/${studentId}`, data),
  getMoodStats: () => api.get("/students/mood-stats"),
};

// Reports
export const reportsAPI = {
  getAnnualReport: (year: number) => api.get(`/reports/annual/${year}`),
};

// Notifications
export const notificationsAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/notifications", { params }),
  markRead: (id: string) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put("/notifications/read-all"),
  requestMentor: (data?: { message?: string }) => api.post("/notifications/mentor-request", data || {}),
  respond: (id: string, data: { response: string }) => api.put(`/notifications/${id}/respond`, data),
  getMyRequests: () => api.get("/notifications/my-requests"),
};

// Audit Logs
export const auditAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/audit", { params }),
  getById: (id: string) => api.get(`/audit/${id}`),
};

// Announcements
export const announcementsAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/announcements", { params }),
  create: (data: Record<string, unknown>) => api.post("/announcements", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/announcements/${id}`, data),
  delete: (id: string) => api.delete(`/announcements/${id}`),
  getAuditLog: (params?: Record<string, unknown>) => api.get("/announcements/audit-log", { params }),
};

// File Upload (Cloudinary)
export const uploadAPI = {
  upload: (file: File, folder?: string) => {
    const formData = new FormData();
    formData.append("file", file);
    if (folder) formData.append("folder", folder);
    return api.post("/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadMultiple: (files: File[], folder?: string) => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    if (folder) formData.append("folder", folder);
    return api.post("/upload/multiple", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  delete: (publicId: string, resourceType?: string) =>
    api.delete("/upload/remove", { params: { publicId, resourceType } }),
};

// Journal
export const journalAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/journal", { params }),
  create: (data: Record<string, unknown>) => api.post("/journal", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/journal/${id}`, data),
  delete: (id: string) => api.delete(`/journal/${id}`),
  getStreak: () => api.get("/journal/streak"),
};

// Attendance
export const attendanceAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/attendance", { params }),
  mark: (data: Record<string, unknown>) => api.post("/attendance", data),
  bulkMark: (data: Record<string, unknown>) => api.post("/attendance/bulk", data),
  getStats: (params?: Record<string, unknown>) => api.get("/attendance/stats", { params }),
  getStudentAttendance: (studentId: string, params?: Record<string, unknown>) => api.get(`/attendance/student/${studentId}`, { params }),
};

// Feedback / Help Desk
export const feedbackAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/feedback", { params }),
  create: (data: Record<string, unknown>) => api.post("/feedback", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/feedback/${id}`, data),
};

// Timetable
export const timetableAPI = {
  getAll: (params?: Record<string, unknown>) => api.get("/timetable", { params }),
  create: (data: Record<string, unknown>) => api.post("/timetable", data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/timetable/${id}`, data),
  delete: (id: string) => api.delete(`/timetable/${id}`),
};

export default api;
